import { Box, Grid2, Paper, Typography } from "@mui/material";
import { UsageMeterData } from "../models/MillOverview2";

type Props = {
	data: UsageMeterData;
};

const formatNumber = (value: number): string => {
	return value.toLocaleString("en-US");
};

type MeterProps = {
	title: string;
	value: number;
	target: number;
	unit: string;
	color: string;
};

const UsageMeter = ({ title, value, target, unit, color }: MeterProps) => {
	const percentage = Math.min((value / target) * 100, 100);
	const barCount = 5;
	const filledBars = Math.round((percentage / 100) * barCount);

	return (
		<Paper
			elevation={1}
			sx={{
				p: 1,
				textAlign: "center",
				backgroundColor: "#FAFAFA",
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography
				variant="caption"
				sx={{
					fontWeight: 600,
					fontSize: "0.65rem",
					display: "block",
					mb: 0.5,
				}}
			>
				{title}: {formatNumber(value)} {unit}
			</Typography>

			<Box
				sx={{
					flexGrow: 1,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: 0.5,
					minHeight: 60,
				}}
			>
				{Array.from({ length: barCount }).map((_, index) => (
					<Box
						key={index}
						sx={{
							width: 12,
							height: "80%",
							backgroundColor: index < filledBars ? color : "#E0E0E0",
							borderRadius: 0.5,
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-end",
						}}
					>
						{index < filledBars && (
							<Box
								sx={{
									height: `${100 - (index / filledBars) * 30}%`,
									backgroundColor: color,
									borderRadius: 0.5,
								}}
							/>
						)}
					</Box>
				))}
			</Box>

			<Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						fontSize: "0.55rem",
						color: "#666",
					}}
				>
					<Typography variant="caption" sx={{ fontSize: "0.55rem" }}>
						Gal/Ton
					</Typography>
					<Typography variant="caption" sx={{ fontSize: "0.55rem" }}>
						S/Ton
					</Typography>
					<Typography variant="caption" sx={{ fontSize: "0.55rem" }}>
						S/Billet
					</Typography>
				</Box>
				<Typography
					variant="caption"
					sx={{ fontSize: "0.55rem", color: "#666", display: "block" }}
				>
					kwh/Ton kwh/Billet
				</Typography>
				<Typography
					variant="caption"
					sx={{ fontSize: "0.55rem", color: "#666", display: "block" }}
				>
					Current Rolling
				</Typography>
			</Box>
		</Paper>
	);
};

export const UsageMeters = ({ data }: Props) => {
	return (
		<Grid2 container spacing={1} sx={{ height: "100%" }}>
			<Grid2 size={{ xs: 4 }} sx={{ display: "flex" }}>
				<UsageMeter
					title="Water Usage"
					value={data.WaterUsage}
					target={data.WaterTarget}
					unit="Gallons"
					color="#2196F3"
				/>
			</Grid2>
			<Grid2 size={{ xs: 4 }} sx={{ display: "flex" }}>
				<UsageMeter
					title="Electricity Usage"
					value={data.ElectricityUsage}
					target={data.ElectricityTarget}
					unit="kwh"
					color="#FF9800"
				/>
			</Grid2>
			<Grid2 size={{ xs: 4 }} sx={{ display: "flex" }}>
				<UsageMeter
					title="Gas Usage"
					value={data.GasUsage}
					target={data.GasTarget}
					unit="mmBtu"
					color="#9C27B0"
				/>
			</Grid2>
		</Grid2>
	);
};
